import { unionBy } from 'lodash';
import {
  CLOSE_LEGEND_EXPANDED,
  CLOSE_LEGEND_PREVIEW,
  MANUALLY_UPDATE_LISTING_BAR,
  OPEN_LEGEND_EXPANDED,
  OPEN_LEGEND_PREVIEW,
  RESET_LISTINGS_INITIAL_LOAD_STATE,
  TOGGLE_LISTING_BAR_CHECKBOX,
  UPDATE_LISTING_BAR,
  UPDATE_LISTINGS,
  UPDATE_PREVIEW_LISTING_BAR,
  UPDATE_PREVIEW_LISTINGS,
} from './actions';
import realEstateTypes from '../../realEstateTypes';
import { EAGERLY_LOADING_LISTINGS_TTL, IS_EAGERLY_LOADING_LISTINGS } from '../../utils/globals';
import localStorage from '../../utils/localStorage';

const { APARTMENT_BUY, APARTMENT_RENT, HOUSE_BUY, HOUSE_RENT } = realEstateTypes;

const initialState = {
  isLegendPreviewOpen: false,
  isLegendExpandedOpen: false,
  isEagerlyLoadingListingBarListings: Boolean(localStorage.getItem(IS_EAGERLY_LOADING_LISTINGS)),
  isInitialLoad: true,
  isInitialPreviewLoad: true,
  listings: {
    houseRentListings: [],
    houseBuyListings: [],
    apartmentRentListings: [],
    apartmentBuyListings: [],
  },
  listingBarListings: {
    houseRentListings: [],
    houseBuyListings: [],
    apartmentRentListings: [],
    apartmentBuyListings: [],
  },
  previewListings: {
    houseRentListings: [],
    houseBuyListings: [],
    apartmentRentListings: [],
    apartmentBuyListings: [],
  },
  listingBarPreviewListings: {
    houseRentListings: [],
    houseBuyListings: [],
    apartmentRentListings: [],
    apartmentBuyListings: [],
  },
};

export const withinBoundingBox = (point, bbox) =>
  bbox.topLeftLat >= point.lat &&
  bbox.bottomRightLat <= point.lat &&
  bbox.topLeftLng <= point.lng &&
  bbox.bottomRightLng >= point.lng;

const processListings = (existinglistings, newListings, boundingBox) => {
  const listingsAlreadyWithinBoundingBox = existinglistings.filter((l) => withinBoundingBox(l.position, boundingBox));
  return unionBy(listingsAlreadyWithinBoundingBox, newListings, 'id');
};

const REAL_ESTATE_TYPES_TO_LISTING_KEYS = {
  [APARTMENT_BUY]: 'apartmentBuyListings',
  [HOUSE_BUY]: 'houseBuyListings',
  [HOUSE_RENT]: 'houseRentListings',
  [APARTMENT_RENT]: 'apartmentRentListings',
};

const dynamicMapReducer = (state = initialState, action) => {
  let realEstateTypeKey;

  switch (action.type) {
    case OPEN_LEGEND_PREVIEW:
      return { ...state, isLegendPreviewOpen: true };
    case CLOSE_LEGEND_PREVIEW:
      return { ...state, isLegendPreviewOpen: false };
    case OPEN_LEGEND_EXPANDED:
      return { ...state, isLegendExpandedOpen: true };
    case CLOSE_LEGEND_EXPANDED:
      return { ...state, isLegendExpandedOpen: false };
    case TOGGLE_LISTING_BAR_CHECKBOX: {
      const listingBarCheckboxState = !state.isEagerlyLoadingListingBarListings;
      localStorage.setItem(IS_EAGERLY_LOADING_LISTINGS, listingBarCheckboxState, EAGERLY_LOADING_LISTINGS_TTL);
      return { ...state, isEagerlyLoadingListingBarListings: listingBarCheckboxState };
    }
    case RESET_LISTINGS_INITIAL_LOAD_STATE:
      return { ...state, isInitialLoad: true, isInitialPreviewLoad: true };
    case UPDATE_LISTINGS:
      realEstateTypeKey = REAL_ESTATE_TYPES_TO_LISTING_KEYS[action.realEstateType];
      if (!realEstateTypeKey) {
        return state;
      }
      return {
        ...state,
        ...{
          listings: {
            ...state.listings,
            [realEstateTypeKey]: processListings(
              state.listings[realEstateTypeKey],
              action.listingsResult[realEstateTypeKey],
              action.boundingBox,
            ),
          },
        },
      };
    case UPDATE_PREVIEW_LISTINGS:
      realEstateTypeKey = REAL_ESTATE_TYPES_TO_LISTING_KEYS[action.realEstateType];
      if (!realEstateTypeKey) {
        return state;
      }
      return {
        ...state,
        ...{
          previewListings: {
            ...state.previewListings,
            [realEstateTypeKey]: processListings(
              state.previewListings[realEstateTypeKey],
              action.listingsResult[realEstateTypeKey],
              action.boundingBox,
            ),
          },
        },
      };

    case UPDATE_LISTING_BAR:
      realEstateTypeKey = REAL_ESTATE_TYPES_TO_LISTING_KEYS[action.realEstateType];
      if (!state.isEagerlyLoadingListingBarListings && !state.isInitialLoad) {
        return state;
      }
      if (!realEstateTypeKey) {
        return state;
      }
      return {
        ...state,
        ...{
          isInitialLoad: false,
          listingBarListings: {
            ...state.listingBarListings,
            [realEstateTypeKey]: processListings(
              state.listingBarListings[realEstateTypeKey],
              action.listingsResult[realEstateTypeKey],
              action.boundingBox,
            ),
          },
        },
      };
    case UPDATE_PREVIEW_LISTING_BAR:
      realEstateTypeKey = REAL_ESTATE_TYPES_TO_LISTING_KEYS[action.realEstateType];
      if (!state.isEagerlyLoadingListingBarListings && !state.isInitialPreviewLoad) {
        return state;
      }
      if (!realEstateTypeKey) {
        return state;
      }
      return {
        ...state,
        ...{
          isInitialPreviewLoad: false,
          listingBarPreviewListings: {
            ...state.listingBarPreviewListings,
            [realEstateTypeKey]: processListings(
              state.listingBarPreviewListings[realEstateTypeKey],
              action.listingsResult[realEstateTypeKey],
              action.boundingBox,
            ),
          },
        },
      };
    case MANUALLY_UPDATE_LISTING_BAR:
      realEstateTypeKey = REAL_ESTATE_TYPES_TO_LISTING_KEYS[action.realEstateType];
      if (!realEstateTypeKey) {
        return state;
      }
      return {
        ...state,
        ...{
          listingBarListings: {
            ...state.listingBarListings,
            [realEstateTypeKey]: state.listings[realEstateTypeKey],
          },
          listingBarPreviewListings: {
            ...state.listingBarPreviewListings,
            [realEstateTypeKey]: state.previewListings[realEstateTypeKey],
          },
        },
      };
    default:
      return state;
  }
};

export default dynamicMapReducer;
